import React, { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import PageHeader from 'components/atoms/PageHeader'
import BackButton from 'components/atoms/BackButton'
import { PRIVATE_PATHS } from 'utils/paths'
import TemplateDataItem from 'features/analytics/compontents/TemplateDataItem'
import { TEMPLATE_ITEMS, TEMPLATE_ITEMS_NAMES } from 'utils/constants'
import { selectAddTemplateFiles, selectAddTemplateValues } from 'features/analytics/ducks/selectors'
import DataDropContainer from 'features/analytics/compontents/DataDropContainer'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { getFileTypes } from 'ducks/files/selectors'
import { selectIsLoadingFileTags, selectIsLoadingFileTypes } from 'ducks/files/selectors'
import { getFileTagsForBpRoutine, getFileTypesRoutine } from 'ducks/files/actions'
import { selectFileCategories } from 'ducks/dictionaries/selectors'
import CategoryPanel from 'features/analytics/compontents/CategoryPanel'
import FilesDropContainer from 'features/analytics/compontents/FilesDropContainer'
import { groupFileTypesForTemplateDisplay } from 'features/analytics/utils/templateFiles'
import PrimaryButton from 'components/atoms/PrimaryButton'
import { createAnalyticsTemplateRoutine } from 'features/analytics/ducks/actions'
import Modal from 'components/atoms/Modal'
import Input from 'components/atoms/Input'
import FullWidthButton from 'features/auth/components/FullWidthButton'
import { isEmpty, values } from 'ramda'
import SearchIcon from '@mui/icons-material/Search'

const AddTemplate = () => {
  const selectedValues = useSelector(selectAddTemplateValues)
  const selectedFilesValues = useSelector(selectAddTemplateFiles)
  const [searchQuery, setSearchQuery] = useState('')
  const dispatch = useDispatch()
  const [name, setName] = useState({
    main: 'New template',
    temp: 'New template'
  })
  const fileTypes = useSelector(getFileTypes)
  const isLoadingFileTypes = useSelector(selectIsLoadingFileTypes)
  const isLoadingFileTags = useSelector(selectIsLoadingFileTags)
  const fileCategories = useSelector(selectFileCategories)
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const selectedBpId = useSelector(state => state.bpDetails?.selectedBp?.id)
  const buildingPassportId = searchParams.get('building_passport_id') || searchParams.get('bpId') || selectedBpId
  const [nameModalOpen, setNameModalOpen] = useState(false)
  const [checklistItems, setChecklistItems] = useState([])

  const reorderItems = items => items.map((item, index) => ({ ...item, sortOrder: index }))

  const handleDataItemDropped = item => {
    const sourceKey = item?.value
    if (!sourceKey) return

    setChecklistItems(prev => {
      const alreadyExists = prev.some(checklistItem => checklistItem.sourceType === 'data-library' && checklistItem.sourceKey === sourceKey)
      if (alreadyExists) return prev

      return reorderItems([
        ...prev,
        {
          id: undefined,
          title: item.label || TEMPLATE_ITEMS_NAMES[sourceKey] || '',
          description: '',
          itemType: 'data',
          fileTypeId: '',
          completionMode: 'creator',
          requiresUpload: false,
          sourceType: 'data-library',
          sourceKey,
          tagRequirements: []
        }
      ])
    })
  }

  const handleDataItemRemoved = sourceKey => {
    setChecklistItems(prev => reorderItems(
      prev.filter(item => !(item.sourceType === 'data-library' && item.sourceKey === sourceKey))
    ))
  }

  const handleFileItemDropped = fileType => {
    if (!fileType?.id) return

    setChecklistItems(prev => {
      const alreadyExists = prev.some(checklistItem =>
        checklistItem.sourceType === 'file-library' &&
        String(checklistItem.fileTypeId) === String(fileType.id)
      )
      if (alreadyExists) return prev

      return reorderItems([
        ...prev,
        {
          id: undefined,
          title: fileType.name || '',
          description: '',
          itemType: 'file',
          fileTypeId: fileType.id,
          completionMode: 'creator',
          requiresUpload: true,
          sourceType: 'file-library',
          sourceKey: fileType.id,
          tagRequirements: []
        }
      ])
    })
  }

  const handleFileItemRemoved = fileTypeId => {
    setChecklistItems(prev => reorderItems(
      prev.filter(item => !(item.sourceType === 'file-library' && String(item.fileTypeId) === String(fileTypeId)))
    ))
  }

  const getSelectedTagsForFile = fileTypeId => {
    const fileItem = checklistItems.find(item => item.sourceType === 'file-library' && String(item.fileTypeId) === String(fileTypeId))
    if (!fileItem) return []
    return (fileItem.tagRequirements || [])
      .map(tag => tag.fileTagId)
      .filter(Boolean)
  }

  const handleFileTagsChange = (fileTypeId, tagIds) => {
    setChecklistItems(prev => prev.map(item => {
      if (!(item.sourceType === 'file-library' && String(item.fileTypeId) === String(fileTypeId))) {
        return item
      }
      return {
        ...item,
        tagRequirements: (tagIds || []).map(tagId => ({
          fileTagId: tagId,
          operator: 'equals',
          groupNo: 0
        }))
      }
    }))
  }

  React.useEffect(() => {
    dispatch(getFileTypesRoutine())
  }, [dispatch])

  React.useEffect(() => {
    if (buildingPassportId) {
      dispatch(getFileTagsForBpRoutine({ bpId: buildingPassportId }))
    }
  }, [dispatch, buildingPassportId])

  const isMetadataLoading = isLoadingFileTypes || (Boolean(buildingPassportId) && isLoadingFileTags)

  const openNameModal = () => setNameModalOpen(true)
  const closeNameModal = () => {
    setNameModalOpen(false)
    setName(prev => ({ ...prev, temp: name.main }))
  }

  const templateItems = useMemo(() => {
    return TEMPLATE_ITEMS.filter(item => !selectedValues[item.value])
  }, [selectedValues])

  const filePanels = useMemo(() => {
    if (fileCategories && fileTypes) {
      const isSearching = !isEmpty(searchQuery)
      const selectedFilesIds = selectedFilesValues.map(e => String(e.id))
      const filteredTypes = fileTypes.filter(type => !selectedFilesIds.includes(String(type.id)))
      const types = isSearching
        ? filteredTypes.filter(type => type.name.toLowerCase().includes(searchQuery.toLowerCase()))
        : filteredTypes

      const categories = groupFileTypesForTemplateDisplay(types, fileCategories)
        .map(category => ({
          ...category,
          types: category.values
        }))

      return isSearching
        ? types.map(type => {
          // random key is a workaround for production issues
          return <TemplateDataItem key={Math.random() * 999} item={type} type='template-file' />
        })
        : categories.map(category => {
          return <CategoryPanel key={category.id} category={category} />
        })
    }
  }, [fileTypes, fileCategories, selectedFilesValues, searchQuery])

  const handleSubmit = () => {
    const preparedItems = checklistItems
      .map((item, index) => ({
        title: (item.title || '').trim(),
        description: (item.description || '').trim() || null,
        itemType: item.itemType || 'file',
        fileTypeId: item.itemType === 'file' ? (item.fileTypeId || null) : null,
        completionMode: item.completionMode || 'creator',
        sortOrder: item.sortOrder ?? index,
        requiresUpload: item.requiresUpload !== false,
        tagRequirements: (item.tagRequirements || [])
          .map(tag => ({
            fileTagId: tag.fileTagId || null,
            tagKey: (tag.tagKey || '').trim() || null,
            tagValue: (tag.tagValue || '').trim() || null,
            operator: tag.operator || 'equals',
            groupNo: Number.isInteger(tag.groupNo) ? tag.groupNo : 0
          }))
          .filter(tag => tag.fileTagId || (tag.tagKey && tag.tagValue))
      }))
      .filter(item => item.title.length > 0)

    dispatch(createAnalyticsTemplateRoutine({
      name: name.main,
      fileTypes: selectedFilesValues.map(e => e.id),
      items: preparedItems,
      ...(buildingPassportId ? { building_passport_id: buildingPassportId } : {}),
      ...selectedValues
    }))
  }

  const shouldDisabledButton = () => {
    const hasChecklistItems = checklistItems.some(item => (item.title || '').trim().length > 0)
    return values(selectedValues).every(val => val === false) && isEmpty(selectedFilesValues) && !hasChecklistItems
  }

  return (
    <PageWrapper>
      <BackButton to={PRIVATE_PATHS.analytics}>Back to Checklists</BackButton>
      <PageHeader>
        Add template
      </PageHeader>
      <PageContent>
        <Header>
          <Title>
            {name.main}
            <EditIcon onClick={openNameModal} />
          </Title>
        </Header>
        <DataDropContainer
          onDataItemDropped={handleDataItemDropped}
          onDataItemRemoved={handleDataItemRemoved}
        />
        <FilesDropContainer
          onFileItemDropped={handleFileItemDropped}
          onFileItemRemoved={handleFileItemRemoved}
          getSelectedTagsForFile={getSelectedTagsForFile}
          onFileTagsChange={handleFileTagsChange}
          isMetaLoading={isMetadataLoading}
        />
        <ButtonWrapper>
          <PrimaryButton
            onClick={handleSubmit}
            disabled={shouldDisabledButton()}
          >
            Add template
          </PrimaryButton>
        </ButtonWrapper>
      </PageContent>
      <SidePanel>
        <SidePanelTitle>
          templates
        </SidePanelTitle>
        <SidePanelDescription>
          Drag and drop to add an item to a custom template
        </SidePanelDescription>
        <SidePanelSectionTitle>
          <span>Data</span>
        </SidePanelSectionTitle>
        <SidePanelTemplatesList>
          {
            templateItems.map(item => {
              return <TemplateDataItem key={item.label} item={item} type='template-data' />
            })
          }
        </SidePanelTemplatesList>
        <SidePanelSectionTitle>
          <span>Files</span>
        </SidePanelSectionTitle>
        <Input
          startAdornment={<SearchIcon />}
          name='searchQuery'
          value={searchQuery}
          onChange={(_, value) => setSearchQuery(value)}
          nomarginbottom
        />
        <SidePanelTemplatesList>
          {filePanels}
        </SidePanelTemplatesList>
      </SidePanel>
      <Modal
        open={nameModalOpen}
        onClose={closeNameModal}
        title='Set template name'
      >
        <Input
          name='name'
          value={name.temp}
          onChange={(_, value) => setName(prev => ({ ...prev, temp: value }))}
        />
        <FullWidthButton
          disabled={isEmpty(name.temp)}
          onClick={() => {
            setName(prev => ({ ...prev, main: name.temp }))
            setNameModalOpen(false)
          }}
        >
          Save
        </FullWidthButton>
      </Modal>
    </PageWrapper>
  )
}

export default AddTemplate

const PageWrapper = styled.div`
  padding-bottom: 50px;
`

const PageContent = styled.div`
  margin-right: 280px;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
  padding: 20px 40px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: bold;
  margin-bottom: 20px;
`

const SidePanel = styled.div`
  width: 280px;
  position: fixed;
  top: ${({ theme }) => theme.dimensions.topBarHeight};
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.lighterGrey};
  padding: 25px 20px;
  overflow-y: auto;
`

const SidePanelTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 5px;
`

const SidePanelDescription = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const SidePanelSectionTitle = styled.div`
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  padding: 10px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey[500]};

  span {
    opacity: .6;
  }
`

const SidePanelTemplatesList = styled.div`
  padding: 10px 0;
`

const EditIcon = styled(EditOutlinedIcon)`
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  transition: all .3s;
  margin-left: 10px;

  &:hover {
    opacity: .8;
  }
`

const Title = styled.div`
  display: flex;
  align-items: center;
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`
